import React, { useMemo } from "react";
import { createPortal } from "react-dom";
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { motion } from "motion/react";
import { ProductEventContext } from "../../adapters/product-events/ProductEventContext.ts";
import { ProductEvent } from "../../adapters/product-events/ProductEventTarget.ts";
import { clx } from "../../utilities/clx.ts";
import styles from "./FloatingCitationWidget.module.css";
import { CitationIcon } from "../MarkdownWithCitations/CitationIcon.tsx";
import { truncateOnWord } from "../HistorySidebar/truncateOnWord.ts";
import { ArrowLeftIcon } from "./icons/ArrowLeftIcon.tsx";
import { ArrowRightIcon } from "./icons/ArrowRightIcon.tsx";
import { PdfIcon } from "./icons/PdfIcon.tsx";
import { getFileType } from "./getFileType.ts";
import { DefaultIcon } from "./icons/DefaultIcon.tsx";
import { CsvIcon } from "./icons/CsvIcon.tsx";
import { DocIcon } from "./icons/DocIcon.tsx";
import { PptIcon } from "./icons/PptIcon.tsx";
import { XlsIcon } from "./icons/XlsIcon.tsx";
import type { CitationComponent } from "../MarkdownWithCitations/MarkdownWithCitations.tsx";

type FloatingCitationProps = Parameters<CitationComponent>[0] & {
  loading?: boolean;
};

export const FloatingCitation: React.FC<FloatingCitationProps> = (props) => {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [isOpen, setIsOpen] = React.useState(false);
  const productEventTarget = React.useContext(ProductEventContext);

  const { citationData, hasData } = useMemo(() => {
    if (!props.data || props.data.length === 0) {
      return { citationData: [], hasData: false };
    }

    const sortedData = props.data
      .map((citation) => ({
        excerpt: citation.excerpt || "",
        id: citation.id,
        page: citation.page?.toString() || "",
        pageNumber: citation.page ? parseInt(citation.page) : undefined,
        title:
          citation.title ||
          "Citation is not available now, please try again later.",
        sourceUrl: citation.sourceUrl,
      }))
      .toSorted(sortByPageNumber);

    return { citationData: dedupe(sortedData), hasData: true };
  }, [props.data]);

  const loading = props.loading ?? false;
  const totalCitations = citationData.length;
  const currentCitation = citationData[currentIndex];

  // Get the chat messages container as boundary for positioning
  const boundary =
    document.querySelector<HTMLElement>("[data-chat-messages-container]") ??
    undefined;

  // Floating UI setup
  const {
    refs,
    floatingStyles,
    context: floatingContext,
  } = useFloating({
    open: isOpen,
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    onOpenChange: (open) => {
      if (!loading) {
        setIsOpen(open);
        if (open) {
          productEventTarget?.dispatchEvent(
            new ProductEvent("citation_expand"),
          );
        }
      }
    },
    middleware: [
      offset(8),
      flip({ boundary, fallbackAxisSideDirection: "start" }),
      shift({ boundary, padding: 8 }),
    ],
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(floatingContext),
    useDismiss(floatingContext, { ancestorScroll: true }),
  ]);

  const handlePrevious = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentIndex < totalCitations - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleOpenInNewTab = () => {
    if (!currentCitation?.sourceUrl) return;
    productEventTarget?.dispatchEvent(new ProductEvent("citation_follow"));
    window.open(currentCitation.sourceUrl, "_blank", "noopener,noreferrer");
  };

  const iconTitle = loading ? "Loading citation..." : "Click to show citation";

  // Convert kebab-case CSS properties to camelCase for React inline styles
  const reactStyles = useMemo(() => {
    const styles: React.CSSProperties = {};
    for (const [key, value] of Object.entries(floatingStyles)) {
      const camelKey = key.replace(/-([a-z])/g, (_, letter) =>
        letter.toUpperCase(),
      );
      styles[camelKey as keyof React.CSSProperties] = value;
    }
    return styles;
  }, [floatingStyles]);

  const fileType = useMemo(() => {
    if (!currentCitation) return "unknown";

    // Try sourceUrl first, then fallback to title
    const urlType = getFileType(currentCitation.sourceUrl);
    if (urlType !== "unknown") return urlType;

    // Fallback to checking the title for file extension
    return getFileType(currentCitation.title);
  }, [currentCitation]);

  const FileIcon = useMemo(() => {
    switch (fileType) {
      case "pdf":
        return PdfIcon;
      case "csv":
        return CsvIcon;
      case "doc":
        return DocIcon;
      case "ppt":
        return PptIcon;
      case "xls":
        return XlsIcon;
      case "unknown":
      default:
        return DefaultIcon;
    }
  }, [fileType]);

  if (!hasData) {
    return null;
  }

  return (
    <>
      <button
        ref={refs.setReference}
        className={clx([styles.CitationTrigger, loading && styles.Loading])}
        aria-label={iconTitle}
        title={iconTitle}
        {...getReferenceProps()}
      >
        <CitationIcon />
      </button>
      {isOpen &&
        createPortal(
          <motion.div
            ref={refs.setFloating}
            {...getFloatingProps()}
            style={reactStyles}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={styles.FloatingCard}
          >
            <Navigation
              handlePrevious={handlePrevious}
              currentIndex={currentIndex}
              handleNext={handleNext}
              totalCitations={totalCitations}
            />

            {currentCitation ? (
              <div className={styles.CitationContent}>
                <div className={styles.TitleRow}>
                  <span className={styles.DocumentIcon}>
                    <FileIcon />
                  </span>
                  <button
                    className={styles.Title}
                    onClick={handleOpenInNewTab}
                    style={{
                      cursor: currentCitation.sourceUrl ? "pointer" : "default",
                    }}
                    title={currentCitation.title}
                    disabled={!currentCitation.sourceUrl}
                  >
                    {currentCitation.title}
                  </button>
                </div>
                {currentCitation.excerpt && (
                  <div className={styles.Excerpt}>
                    {truncateOnWord(currentCitation.excerpt, 100)}
                  </div>
                )}
                {currentCitation.page && (
                  <span className={styles.PageNumber}>
                    Page {currentCitation.page}
                  </span>
                )}
              </div>
            ) : (
              <LoadingSkeleton />
            )}
          </motion.div>,
          document.body,
        )}
    </>
  );
};

const LoadingSkeleton = () => (
  <div className={styles.CitationContent}>
    <div className={styles.TitleRow}>
      <span className={styles.DocumentIcon}>
        <PdfIcon />
      </span>
      <div
        className={clx([styles.Title, styles.Skeleton, styles.SkeletonBar])}
      />
    </div>
    <div className={clx([styles.Excerpt, styles.Skeleton])}>
      <div className={clx([styles.ExcerptLine, styles.SkeletonBar])} />
      <div className={clx([styles.ExcerptLine, styles.SkeletonBar])} />
    </div>
    <span
      className={clx([styles.PageNumber, styles.Skeleton, styles.SkeletonBar])}
    />
  </div>
);

const sortByPageNumber = (
  a: {
    pageNumber?: number;
  },
  b: {
    pageNumber?: number;
  },
): number => {
  const pageA = a.pageNumber;
  const pageB = b.pageNumber;
  if (pageA != null && pageB != null) return pageA - pageB;
  if (pageA != null) return -1;
  if (pageB != null) return 1;
  return 0;
};

const dedupe = (
  sortedData: {
    excerpt: string;
    id: string;
    page: string;
    pageNumber: number | undefined;
    title: string;
    sourceUrl: string | undefined;
  }[],
) => {
  const uniqueCitations: typeof sortedData = [];
  const seen = new Set<string>();

  for (const citation of sortedData) {
    const key = JSON.stringify([citation.page, citation.excerpt]);

    if (!seen.has(key)) {
      seen.add(key);
      uniqueCitations.push(citation);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const citationData = uniqueCitations.map(({ pageNumber, ...rest }) => rest);
  return citationData;
};

function Navigation(
  props: Readonly<{
    currentIndex: number;
    totalCitations: number;
    handlePrevious: (e: React.MouseEvent) => void;
    handleNext: (e: React.MouseEvent) => void;
  }>,
) {
  return (
    <div className={styles.Navigation}>
      <button
        className={styles.NavButton}
        onClick={props.handlePrevious}
        disabled={props.currentIndex === 0}
        aria-label="Previous citation"
      >
        <ArrowLeftIcon />
      </button>
      <button
        className={styles.NavButton}
        onClick={props.handleNext}
        disabled={props.currentIndex === props.totalCitations - 1}
        aria-label="Next citation"
      >
        <ArrowRightIcon />
      </button>
      <span className={styles.PageIndicator}>
        {props.currentIndex + 1}/{props.totalCitations}
      </span>
    </div>
  );
}
